import { useMemo, useState } from 'react';
import { Factory, ChevronDown, Check, Trophy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { loadPricing, findUnitCost } from '@/lib/pricing';
import type { ShiftLog, Consumable } from '@/types/drilling';

interface BitPerformanceEntry {
    name: string;
    metres: number;
    manufacturer: string;
    label: string;
}

interface BitPerformanceGroup {
    diameter: number;
    label: string;
    top: BitPerformanceEntry[];
    bottom: BitPerformanceEntry[];
}

interface ConsumablePerformanceGroup {
    size: string;
    top: BitPerformanceEntry[];
    bottom: BitPerformanceEntry[];
}

interface ConsumableTablesProps {
    logs: ShiftLog[];
    bitPerformance?: BitPerformanceGroup[];
    hammerPerformance?: ConsumablePerformanceGroup[];
    rodPerformance?: ConsumablePerformanceGroup[];
}

// A unique physical unit (one serial number), deduped across shifts.
interface Unit {
    serial: string;
    manufacturer: string;
    metres: number;
    diameter: number;
    consumable: Consumable; // representative entry, for pricing lookups
}

interface MfrRow {
    manufacturer: string;
    units: number;
    totalMetres: number;
    avgMetres: number;
}

// Dedupe consumables of a given type by serialNumber. metresDrilled is
// cumulative per physical unit across shifts, so we keep the max value seen
// (mirrors the aggregation in UniversalDashboard / DrillingCharts).
function dedupeUnits(logs: ShiftLog[], type: 'drill-bit' | 'hammer' | 'drill-rod'): Unit[] {
    const bySerial: Record<string, Unit> = {};
    for (const log of logs) {
        for (const c of log.consumables ?? []) {
            if (c.type !== type || !c.serialNumber) continue;
            const existing = bySerial[c.serialNumber];
            const metres = c.metresDrilled ?? 0;
            if (!existing || metres > existing.metres) {
                bySerial[c.serialNumber] = {
                    serial: c.serialNumber,
                    manufacturer: c.manufacturer || 'Unknown',
                    metres,
                    diameter: c.bitDiameter ?? 0,
                    consumable: c,
                };
            }
        }
    }
    return Object.values(bySerial);
}

// Roll deduped units up to one row per manufacturer.
function rowsByManufacturer(units: Unit[]): MfrRow[] {
    const map: Record<string, { units: number; totalMetres: number }> = {};
    for (const u of units) {
        if (!map[u.manufacturer]) map[u.manufacturer] = { units: 0, totalMetres: 0 };
        map[u.manufacturer].units += 1;
        map[u.manufacturer].totalMetres += u.metres;
    }
    return Object.entries(map)
        .map(([manufacturer, d]) => ({
            manufacturer,
            units: d.units,
            totalMetres: Math.round(d.totalMetres),
            avgMetres: d.units > 0 ? Math.round(d.totalMetres / d.units) : 0,
        }))
        .sort((a, b) => b.totalMetres - a.totalMetres);
}

function UsageTable({ rows }: { rows: MfrRow[] }) {
    if (rows.length === 0) {
        return (
            <p className='text-sm text-muted-foreground py-4'>No data for the selected filters.</p>
        );
    }
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Manufacturer</TableHead>
                    <TableHead className='text-right'>Units used</TableHead>
                    <TableHead className='text-right'>Total metres</TableHead>
                    <TableHead className='text-right'>Avg m/unit</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {rows.map((r) => (
                    <TableRow key={r.manufacturer}>
                        <TableCell className='font-medium text-foreground'>
                            {r.manufacturer}
                        </TableCell>
                        <TableCell className='text-right'>{r.units}</TableCell>
                        <TableCell className='text-right'>
                            {r.totalMetres.toLocaleString()}
                        </TableCell>
                        <TableCell className='text-right'>{r.avgMetres.toLocaleString()}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

// One manufacturer's standing within a diameter group. Rows arrive pre-sorted
// best-first, so index 0 is the winner.
interface LeaderRow {
    manufacturer: string;
    value: string; // the ranked metric, formatted
    trailing: string[]; // extra right-aligned cells (e.g. units, metres)
}
interface LeaderGroup {
    diameter: number;
    rows: LeaderRow[];
}

// Shared renderer for the CPM and pen-rate leaderboards so they look identical.
// Manufacturers are grouped by diameter; the top row of each group is the
// winner and gets the trophy + accent treatment.
function LeaderboardTable({
    valueHeader,
    trailingHeaders,
    groups,
}: {
    valueHeader: string;
    trailingHeaders: string[];
    groups: LeaderGroup[];
}) {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Diameter</TableHead>
                    <TableHead>Manufacturer</TableHead>
                    <TableHead className='text-right'>{valueHeader}</TableHead>
                    {trailingHeaders.map((h) => (
                        <TableHead key={h} className='text-right'>
                            {h}
                        </TableHead>
                    ))}
                </TableRow>
            </TableHeader>
            <TableBody>
                {groups.flatMap((group) =>
                    group.rows.map((r, idx) => {
                        const isWinner = idx === 0;
                        return (
                            <TableRow
                                key={`${group.diameter}-${r.manufacturer}`}
                                className={cn(isWinner && 'bg-primary/5')}
                            >
                                <TableCell className='text-muted-foreground'>
                                    {idx === 0 ? `${group.diameter}mm` : ''}
                                </TableCell>
                                <TableCell className='font-medium text-foreground'>
                                    <span className='flex items-center gap-1.5'>
                                        {isWinner && (
                                            <Trophy className='h-3.5 w-3.5 text-primary' />
                                        )}
                                        {r.manufacturer}
                                    </span>
                                </TableCell>
                                <TableCell
                                    className={cn(
                                        'text-right tabular-nums',
                                        isWinner && 'font-semibold text-primary'
                                    )}
                                >
                                    {r.value}
                                </TableCell>
                                {r.trailing.map((t, i) => (
                                    <TableCell key={i} className='text-right'>
                                        {t}
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })
                )}
            </TableBody>
        </Table>
    );
}

export default function ConsumableTables({ logs, bitPerformance = [], hammerPerformance = [], rodPerformance = [] }: ConsumableTablesProps) {
    const [selectedMfrs, setSelectedMfrs] = useState<string[]>([]);

    const bitUnits = useMemo(() => dedupeUnits(logs, 'drill-bit'), [logs]);
    const hammerUnits = useMemo(() => dedupeUnits(logs, 'hammer'), [logs]);
    const rodUnits = useMemo(() => dedupeUnits(logs, 'drill-rod'), [logs]);

    // Manufacturers present across bits, hammers, and rods for the filter.
    const availableMfrs = useMemo(() => {
        const set = new Set<string>();
        bitUnits.forEach((u) => set.add(u.manufacturer));
        hammerUnits.forEach((u) => set.add(u.manufacturer));
        rodUnits.forEach((u) => set.add(u.manufacturer));
        return [...set].sort();
    }, [bitUnits, hammerUnits, rodUnits]);

    const bitRows = useMemo(
        () =>
            rowsByManufacturer(
                bitUnits.filter(
                    (u) => selectedMfrs.length === 0 || selectedMfrs.includes(u.manufacturer)
                )
            ),
        [bitUnits, selectedMfrs]
    );
    const rodRows = useMemo(
        () =>
            rowsByManufacturer(
                rodUnits.filter(
                    (u) => selectedMfrs.length === 0 || selectedMfrs.includes(u.manufacturer)
                )
            ),
        [rodUnits, selectedMfrs]
    );

    // Hammers grouped by diameter for a simple size breakdown.
    const hammersByDiameter = useMemo(() => {
        const filtered = hammerUnits.filter(
            (u) => selectedMfrs.length === 0 || selectedMfrs.includes(u.manufacturer)
        );
        const map: Record<number, { units: number; totalMetres: number }> = {};
        for (const u of filtered) {
            if (u.diameter <= 0) continue;
            if (!map[u.diameter]) map[u.diameter] = { units: 0, totalMetres: 0 };
            map[u.diameter].units += 1;
            map[u.diameter].totalMetres += u.metres;
        }
        return Object.entries(map)
            .map(([dia, d]) => ({
                diameter: Number(dia),
                label: `${Math.round(Number(dia) / 25.4)}"`,
                units: d.units,
                totalMetres: Math.round(d.totalMetres),
                avgMetres: d.units > 0 ? Math.round(d.totalMetres / d.units) : 0,
            }))
            .sort((a, b) => a.diameter - b.diameter);
    }, [hammerUnits, selectedMfrs]);

    // Both leaderboards are drill-bits-only and ignore the manufacturer filter,
    // so they always rank the full field.

    // CPM leaderboard — per diameter, manufacturers ranked ascending by
    // cost-per-metre = Σ(unit cost) / Σ(metres). Uses deduped serial units.
    const pricing = useMemo(() => loadPricing(), [logs]);
    const hasPricing = pricing.length > 0;

    const cpmLeaderboard = useMemo<LeaderGroup[]>(() => {
        // diameter -> manufacturer -> { cost, metres, units }
        const map: Record<
            number,
            Record<string, { cost: number; metres: number; units: number }>
        > = {};
        for (const u of bitUnits) {
            if (u.metres <= 0 || u.diameter <= 0) continue;
            const dia = u.diameter;
            if (!map[dia]) map[dia] = {};
            if (!map[dia][u.manufacturer])
                map[dia][u.manufacturer] = { cost: 0, metres: 0, units: 0 };
            map[dia][u.manufacturer].cost += findUnitCost(u.consumable, pricing);
            map[dia][u.manufacturer].metres += u.metres;
            map[dia][u.manufacturer].units += 1;
        }
        return Object.entries(map)
            .map(([dia, mfrs]) => ({
                diameter: Number(dia),
                rows: Object.entries(mfrs)
                    .map(([manufacturer, d]) => ({
                        manufacturer,
                        cpm: d.metres > 0 ? Math.round((d.cost / d.metres) * 100) / 100 : 0,
                        units: d.units,
                        metres: Math.round(d.metres),
                    }))
                    .filter((r) => r.cpm > 0)
                    .sort((a, b) => a.cpm - b.cpm)
                    .map((r) => ({
                        manufacturer: r.manufacturer,
                        value: `$${r.cpm.toFixed(2)}`,
                        trailing: [r.units.toLocaleString(), r.metres.toLocaleString()],
                    })),
            }))
            .filter((g) => g.rows.length > 0)
            .sort((a, b) => a.diameter - b.diameter);
    }, [bitUnits, pricing]);

    // Penetration-rate leaderboard — per diameter, manufacturers ranked
    // descending by avg m/hr. Pen rate is derived from each bit's linked holes
    // (depth ÷ drilling time) within its shift, mirroring bitManufacturerData in
    // UniversalDashboard — NOT from the deduped serial metres.
    const penRateLeaderboard = useMemo<LeaderGroup[]>(() => {
        // diameter -> manufacturer -> per-bit pen-rate samples
        const samples: Record<number, Record<string, number[]>> = {};
        for (const log of logs) {
            for (const c of log.consumables ?? []) {
                if (c.type !== 'drill-bit' || !c.manufacturer) continue;
                const linked = c.linkedHoleIds ?? [];
                const holes =
                    linked.length > 0
                        ? (log.drillHoles ?? []).filter((h) => linked.includes(h.id))
                        : (log.drillHoles ?? []);
                const totalM = holes.reduce((s, h) => s + (h.depth ?? 0), 0);
                const totalS = holes.reduce((s, h) => s + (h.drillingTimeSeconds ?? 0), 0);
                if (totalS <= 0) continue;
                const dia = c.bitDiameter ?? 0;
                if (dia <= 0) continue;
                if (!samples[dia]) samples[dia] = {};
                if (!samples[dia][c.manufacturer]) samples[dia][c.manufacturer] = [];
                samples[dia][c.manufacturer].push((totalM / totalS) * 3600);
            }
        }
        return Object.entries(samples)
            .map(([dia, mfrs]) => ({
                diameter: Number(dia),
                rows: Object.entries(mfrs)
                    .map(([manufacturer, vals]) => ({
                        manufacturer,
                        penRate:
                            Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10,
                        count: vals.length,
                    }))
                    .filter((r) => r.penRate > 0)
                    .sort((a, b) => b.penRate - a.penRate)
                    .map((r) => ({
                        manufacturer: r.manufacturer,
                        value: `${r.penRate.toFixed(1)} m/hr`,
                        trailing: [r.count.toLocaleString()],
                    })),
            }))
            .filter((g) => g.rows.length > 0)
            .sort((a, b) => a.diameter - b.diameter);
    }, [logs]);

    // Overall CPM leaderboard — total cost-per-metre by manufacturer across all
    // consumable types and diameters for the period.
    const overallCpmByMfr = useMemo(() => {
        const map: Record<string, { cost: number; metres: number; units: number }> = {};
        for (const u of [...bitUnits, ...hammerUnits, ...rodUnits]) {
            if (u.metres <= 0) continue;
            if (!map[u.manufacturer]) map[u.manufacturer] = { cost: 0, metres: 0, units: 0 };
            map[u.manufacturer].cost += findUnitCost(u.consumable, pricing);
            map[u.manufacturer].metres += u.metres;
            map[u.manufacturer].units += 1;
        }
        return Object.entries(map)
            .map(([manufacturer, d]) => ({
                manufacturer,
                cpm: d.metres > 0 ? Math.round((d.cost / d.metres) * 100) / 100 : 0,
                units: d.units,
                totalMetres: Math.round(d.metres),
            }))
            .filter((r) => r.cpm > 0)
            .sort((a, b) => a.cpm - b.cpm);
    }, [bitUnits, hammerUnits, rodUnits, pricing]);

    return (
        <div className='space-y-6 mb-6'>
            <div className='flex items-center justify-between gap-3 flex-wrap'>
                <h3 className='text-base font-semibold text-foreground'>
                    Consumables by Manufacturer
                </h3>

                {/* Manufacturer multi-select — applies to the usage tables */}
                {availableMfrs.length > 0 && (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant='outline' size='sm' className='gap-2'>
                                <Factory className='h-4 w-4' />
                                {selectedMfrs.length === 0
                                    ? 'All Manufacturers'
                                    : selectedMfrs.length === 1
                                      ? selectedMfrs[0]
                                      : `${selectedMfrs.length} manufacturers`}
                                <ChevronDown className='h-3 w-3 opacity-60' />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className='w-64 p-2 z-50' align='end'>
                            {selectedMfrs.length > 0 && (
                                <button
                                    className='w-full text-left text-xs text-muted-foreground px-2 py-1.5 hover:text-foreground mb-1'
                                    onClick={() => setSelectedMfrs([])}
                                >
                                    Clear selection
                                </button>
                            )}
                            {availableMfrs.map((mfr) => {
                                const checked = selectedMfrs.includes(mfr);
                                return (
                                    <button
                                        key={mfr}
                                        className='flex items-center gap-2 w-full text-left text-sm px-2 py-1.5 rounded hover:bg-accent'
                                        onClick={() =>
                                            setSelectedMfrs(
                                                checked
                                                    ? selectedMfrs.filter((m) => m !== mfr)
                                                    : [...selectedMfrs, mfr]
                                            )
                                        }
                                    >
                                        <Check
                                            className={cn(
                                                'h-4 w-4 shrink-0',
                                                checked ? 'opacity-100' : 'opacity-0'
                                            )}
                                        />
                                        {mfr}
                                    </button>
                                );
                            })}
                        </PopoverContent>
                    </Popover>
                )}
            </div>

            {/* Usage tables — Bits, Hammers, Rods */}
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                <Card className='bg-card border-border'>
                    <CardHeader>
                        <CardTitle className='text-foreground text-base'>Drill Bits</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <UsageTable rows={bitRows} />
                    </CardContent>
                </Card>

                <Card className='bg-card border-border'>
                    <CardHeader>
                        <CardTitle className='text-foreground text-base'>Hammers</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {hammersByDiameter.length === 0 ? (
                            <p className='text-sm text-muted-foreground py-4'>No hammer data for the selected period.</p>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Size</TableHead>
                                        <TableHead className='text-right'>Units used</TableHead>
                                        <TableHead className='text-right'>Total metres</TableHead>
                                        <TableHead className='text-right'>Avg m/unit</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {hammersByDiameter.map((r) => (
                                        <TableRow key={r.diameter}>
                                            <TableCell className='font-medium text-foreground'>
                                                {r.label}
                                            </TableCell>
                                            <TableCell className='text-right'>{r.units}</TableCell>
                                            <TableCell className='text-right'>
                                                {r.totalMetres.toLocaleString()}
                                            </TableCell>
                                            <TableCell className='text-right'>{r.avgMetres.toLocaleString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                <Card className='bg-card border-border'>
                    <CardHeader>
                        <CardTitle className='text-foreground text-base'>Drill Rods</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <UsageTable rows={rodRows} />
                    </CardContent>
                </Card>
            </div>

            {/* Overall CPM by Manufacturer — all consumables */}
            <Card className='bg-card border-border'>
                <CardHeader>
                    <CardTitle className='text-foreground text-base flex items-center gap-2'>
                        <Trophy className='h-4 w-4' />
                        Cost-Per-Metre Leaderboard — All Consumables by Manufacturer
                    </CardTitle>
                    {!hasPricing && (
                        <p className='text-sm text-muted-foreground'>
                            Add consumable pricing in <strong>Settings</strong> to see cost data.
                        </p>
                    )}
                </CardHeader>
                <CardContent>
                    {overallCpmByMfr.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className='w-8'></TableHead>
                                    <TableHead>Manufacturer</TableHead>
                                    <TableHead className='text-right'>CPM ($/m)</TableHead>
                                    <TableHead className='text-right'>Units</TableHead>
                                    <TableHead className='text-right'>Total metres</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {overallCpmByMfr.map((r, idx) => (
                                    <TableRow
                                        key={r.manufacturer}
                                        className={cn(idx === 0 && 'bg-primary/5')}
                                    >
                                        <TableCell>
                                            {idx === 0 && (
                                                <Trophy className='h-3.5 w-3.5 text-primary' />
                                            )}
                                        </TableCell>
                                        <TableCell className='font-medium text-foreground'>
                                            {r.manufacturer}
                                        </TableCell>
                                        <TableCell
                                            className={cn(
                                                'text-right tabular-nums',
                                                idx === 0 && 'font-semibold text-primary'
                                            )}
                                        >
                                            ${r.cpm.toFixed(2)}
                                        </TableCell>
                                        <TableCell className='text-right'>{r.units}</TableCell>
                                        <TableCell className='text-right'>
                                            {r.totalMetres.toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <p className='text-sm text-muted-foreground py-4'>
                            {hasPricing
                                ? 'No consumable data for the selected period.'
                                : 'Pricing not configured.'}
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Manufacturer leaderboards — cheapest (CPM) and fastest (pen rate) per
          diameter, side by side for direct comparison. Drill bits only. */}
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Penetration-rate leaderboard — fastest manufacturer per diameter */}
                <Card className='bg-card border-border'>
                    <CardHeader>
                        <CardTitle className='text-foreground text-base flex items-center gap-2'>
                            <Trophy className='h-4 w-4' />
                            Penetration Rate Leaderboard — Bits by Diameter
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {penRateLeaderboard.length > 0 ? (
                            <LeaderboardTable
                                valueHeader='Avg m/hr'
                                trailingHeaders={['Bits']}
                                groups={penRateLeaderboard}
                            />
                        ) : (
                            <p className='text-sm text-muted-foreground py-4'>
                                No drilling-time data for the selected period.
                            </p>
                        )}
                    </CardContent>
                </Card>

                {/* CPM leaderboard — cheapest manufacturer per diameter */}
                <Card className='bg-card border-border'>
                    <CardHeader className='flex flex-row items-start justify-between space-y-0 gap-2'>
                        <CardTitle className='text-foreground text-base flex items-center gap-2'>
                            <Trophy className='h-4 w-4' />
                            Cost-Per-Metre Leaderboard — Bits by Diameter
                        </CardTitle>
                        {!hasPricing && (
                            <p className='text-sm text-muted-foreground text-right'>
                                Add consumable pricing in <strong>Settings</strong> to see cost
                                data.
                            </p>
                        )}
                    </CardHeader>
                    <CardContent>
                        {hasPricing && cpmLeaderboard.length > 0 ? (
                            <LeaderboardTable
                                valueHeader='CPM ($/m)'
                                trailingHeaders={['Units', 'Metres']}
                                groups={cpmLeaderboard}
                            />
                        ) : (
                            <p className='text-sm text-muted-foreground py-4'>
                                {hasPricing
                                    ? 'No priced drill-bit data for the selected period.'
                                    : 'Pricing not configured.'}
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Drill Bit Performance — Top 5 / Bottom 5 per diameter */}
            <Card className='bg-card border-border'>
                <CardHeader>
                    <CardTitle className='text-foreground text-base'>Drill Bit Performance</CardTitle>
                </CardHeader>
                <CardContent>
                    {bitPerformance.length > 0 ? (
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                            {bitPerformance.map((group) => (
                                <div key={group.diameter} className='space-y-2'>
                                    <h4 className='text-sm font-medium text-foreground'>
                                        {group.label} — Top 5 / Bottom 5
                                    </h4>
                                    <div className='overflow-x-auto'>
                                        <table className='w-full text-sm'>
                                            <thead>
                                                <tr className='border-b border-border text-left text-muted-foreground'>
                                                    <th className='pb-1.5 font-medium w-8'>#</th>
                                                    <th className='pb-1.5 font-medium'>Serial</th>
                                                    <th className='pb-1.5 font-medium'>Manufacturer</th>
                                                    <th className='pb-1.5 font-medium text-right'>Metres</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.top.map((entry, i) => (
                                                    <tr key={entry.name} className='border-b border-border/30'>
                                                        <td className='py-1.5 text-muted-foreground'>{i + 1}</td>
                                                        <td className='py-1.5 font-medium text-foreground'>{entry.name}</td>
                                                        <td className='py-1.5 text-muted-foreground'>{entry.manufacturer}</td>
                                                        <td className='py-1.5 text-right'>
                                                            <span className='text-green-500 font-medium'>{entry.metres.toLocaleString()}m</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {group.bottom.length > 0 && (
                                                    <>
                                                        {group.top.length > 0 && (
                                                            <tr>
                                                                <td colSpan={4} className='py-1 text-center text-xs text-muted-foreground/60'>···</td>
                                                            </tr>
                                                        )}
                                                        {group.bottom.map((entry) => (
                                                            <tr key={entry.name} className='border-b border-border/30 last:border-0'>
                                                                <td className='py-1.5 text-muted-foreground' />
                                                                <td className='py-1.5 font-medium text-foreground'>{entry.name}</td>
                                                                <td className='py-1.5 text-muted-foreground'>{entry.manufacturer}</td>
                                                                <td className='py-1.5 text-right'>
                                                                    <span className='text-red-500 font-medium'>{entry.metres.toLocaleString()}m</span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground py-4'>
                            No drill-bit serial data for the selected period.
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Hammer Performance — Top 3 / Bottom 3 per size */}
            <Card className='bg-card border-border'>
                <CardHeader>
                    <CardTitle className='text-foreground text-base'>Hammer Performance</CardTitle>
                </CardHeader>
                <CardContent>
                    {hammerPerformance.length > 0 ? (
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                            {hammerPerformance.map((group) => (
                                <div key={group.size} className='space-y-2'>
                                    <h4 className='text-sm font-medium text-foreground'>
                                        {group.size} — Top 3 / Bottom 3
                                    </h4>
                                    <div className='overflow-x-auto'>
                                        <table className='w-full text-sm'>
                                            <thead>
                                                <tr className='border-b border-border text-left text-muted-foreground'>
                                                    <th className='pb-1.5 font-medium w-8'>#</th>
                                                    <th className='pb-1.5 font-medium'>Serial</th>
                                                    <th className='pb-1.5 font-medium'>Manufacturer</th>
                                                    <th className='pb-1.5 font-medium text-right'>Metres</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.top.map((entry, i) => (
                                                    <tr key={entry.name} className='border-b border-border/30'>
                                                        <td className='py-1.5 text-muted-foreground'>{i + 1}</td>
                                                        <td className='py-1.5 font-medium text-foreground'>{entry.name}</td>
                                                        <td className='py-1.5 text-muted-foreground'>{entry.manufacturer}</td>
                                                        <td className='py-1.5 text-right'>
                                                            <span className='text-green-500 font-medium'>{entry.metres.toLocaleString()}m</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {group.bottom.length > 0 && (
                                                    <>
                                                        {group.top.length > 0 && (
                                                            <tr>
                                                                <td colSpan={4} className='py-1 text-center text-xs text-muted-foreground/60'>···</td>
                                                            </tr>
                                                        )}
                                                        {group.bottom.map((entry) => (
                                                            <tr key={entry.name} className='border-b border-border/30 last:border-0'>
                                                                <td className='py-1.5 text-muted-foreground' />
                                                                <td className='py-1.5 font-medium text-foreground'>{entry.name}</td>
                                                                <td className='py-1.5 text-muted-foreground'>{entry.manufacturer}</td>
                                                                <td className='py-1.5 text-right'>
                                                                    <span className='text-red-500 font-medium'>{entry.metres.toLocaleString()}m</span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground py-4'>
                            No hammer serial data for the selected period.
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Rod Performance — Top 5 / Bottom 5 per size */}
            <Card className='bg-card border-border'>
                <CardHeader>
                    <CardTitle className='text-foreground text-base'>Drill Rod Performance</CardTitle>
                </CardHeader>
                <CardContent>
                    {rodPerformance.length > 0 ? (
                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                            {rodPerformance.map((group) => (
                                <div key={group.size} className='space-y-2'>
                                    <h4 className='text-sm font-medium text-foreground'>
                                        {group.size} — Top 5 / Bottom 5
                                    </h4>
                                    <div className='overflow-x-auto'>
                                        <table className='w-full text-sm'>
                                            <thead>
                                                <tr className='border-b border-border text-left text-muted-foreground'>
                                                    <th className='pb-1.5 font-medium w-8'>#</th>
                                                    <th className='pb-1.5 font-medium'>Serial</th>
                                                    <th className='pb-1.5 font-medium'>Manufacturer</th>
                                                    <th className='pb-1.5 font-medium text-right'>Metres</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.top.map((entry, i) => (
                                                    <tr key={entry.name} className='border-b border-border/30'>
                                                        <td className='py-1.5 text-muted-foreground'>{i + 1}</td>
                                                        <td className='py-1.5 font-medium text-foreground'>{entry.name}</td>
                                                        <td className='py-1.5 text-muted-foreground'>{entry.manufacturer}</td>
                                                        <td className='py-1.5 text-right'>
                                                            <span className='text-green-500 font-medium'>{entry.metres.toLocaleString()}m</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {group.bottom.length > 0 && (
                                                    <>
                                                        {group.top.length > 0 && (
                                                            <tr>
                                                                <td colSpan={4} className='py-1 text-center text-xs text-muted-foreground/60'>···</td>
                                                            </tr>
                                                        )}
                                                        {group.bottom.map((entry) => (
                                                            <tr key={entry.name} className='border-b border-border/30 last:border-0'>
                                                                <td className='py-1.5 text-muted-foreground' />
                                                                <td className='py-1.5 font-medium text-foreground'>{entry.name}</td>
                                                                <td className='py-1.5 text-muted-foreground'>{entry.manufacturer}</td>
                                                                <td className='py-1.5 text-right'>
                                                                    <span className='text-red-500 font-medium'>{entry.metres.toLocaleString()}m</span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground py-4'>
                            No drill-rod serial data for the selected period.
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
